/**
 * Task field controls — spec §8.4, shared by the New-task modal (V3 R3) and the
 * task detail workbench so the two cannot drift.
 *
 * Every control is presentational: it takes a value and an onChange. Permission
 * gating and persistence belong to the caller, because the modal writes on
 * submit and the detail page writes on change.
 */

import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { PRIORITIES, type Priority, type TaskType, TASK_TYPES, SEMANTIC_SUCCESS } from '@virtuetasks/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { UserAvatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useUsers } from '@/features/settings/api';
import { useLabels, useSuggestedAssignees, useTasks, type TaskView } from './api';

export const PRIORITY_COLOUR: Record<string, string> = {
  P0: '#C2413A',
  P1: '#C9762B',
  P2: '#4A7CB5',
  P3: '#7C838A',
};

const PRIORITY_MEANING: Record<string, string> = {
  P0: 'Drop everything',
  P1: 'This week',
  P2: 'Normal',
  P3: 'Whenever',
};

export const CATEGORY_COLOUR: Record<string, string> = {
  todo: '#7C838A',
  in_progress: '#C9762B',
  done: SEMANTIC_SUCCESS,
  cancelled: '#9BA1A8',
};

function FieldButton({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'flex h-8 min-w-0 items-center gap-1.5 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-left text-[13px] transition-colors duration-[120ms] hover:bg-[var(--surface-2)]',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * The popover every field picker opens — V10 §2.
 *
 * `modal` is the whole fix, and it is not a styling preference.
 *
 * Radix portals popover content to `document.body`. A modal `Dialog` — the
 * New-task modal — locks scrolling with `react-remove-scroll`, which cancels
 * every wheel event whose target is outside the locked subtree, exempting only
 * the shard it was given (the dialog's own content). A picker's list is
 * portalled *out* of that subtree, so with fifteen members its list clipped at
 * `max-h-64` and then refused to scroll: everyone below the fold was
 * unreachable, by mouse and by trackpad, in every picker in the modal.
 *
 * A `modal` popover wraps its own content in `RemoveScroll`, which makes the
 * list the locked region rather than a victim of one. One component, so the
 * three people pickers — Assignees, Reviewer, Watchers — and the label and task
 * pickers cannot drift apart on it again.
 */
function FieldPopover({
  open,
  onOpenChange,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange} modal>
      {children}
    </Popover>
  );
}

/**
 * How tall a picker's list may be.
 *
 * `--radix-popover-content-available-height` is the room Radix measured
 * between the trigger and the viewport edge, so the list uses the space it
 * actually has instead of a fixed 16rem that can overflow a short window —
 * and never grows past 20rem, because a list taller than that is a scroll
 * problem rather than a scrolling one.
 */
const PICKER_LIST_HEIGHT =
  'max-h-[min(20rem,var(--radix-popover-content-available-height,20rem))] overflow-y-auto overscroll-contain';

/* ── Status ────────────────────────────────────────────── */

export interface StatusOption {
  id: string;
  name: string;
  category: string;
  wipLimit?: number | null;
  requiresReason?: boolean;
}

export function StatusSelect({
  statuses,
  value,
  onChange,
  disabled,
  testId = 'task-status-select',
}: {
  statuses: StatusOption[];
  value?: string;
  onChange: (statusId: string, reason?: string) => void;
  disabled?: boolean;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const current = statuses.find((s) => s.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* V4 D-2: the control itself carries the signal too, so a done task
            reads as done from the side panel as well as from the banner. */}
        <FieldButton
          disabled={disabled}
          data-testid={testId}
          aria-label="Status"
          data-category={current?.category}
          className={
            current?.category === 'done'
              ? 'border-[var(--accent)]/40 bg-[var(--accent-soft)] text-[var(--accent)]'
              : undefined
          }
        >
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ background: CATEGORY_COLOUR[current?.category ?? 'todo'] }}
            aria-hidden
          />
          <span className="truncate">{current?.name ?? 'No status'}</span>
          <ChevronDown className="ml-auto size-3.5 shrink-0 opacity-60" />
        </FieldButton>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        <div role="listbox" aria-label="Status">
          {statuses.map((status) => (
            <button
              key={status.id}
              type="button"
              role="option"
              aria-selected={status.id === value}
              onClick={() => {
                // §4.3: a status flagged `requiresReason` (Blocked) must not be
                // enterable without one.
                let reason: string | undefined;
                if (status.requiresReason) {
                  reason = window.prompt('Why is this blocked?') ?? undefined;
                  if (!reason?.trim()) return;
                }
                onChange(status.id, reason);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[13px] hover:bg-[var(--surface-2)]"
            >
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: CATEGORY_COLOUR[status.category] }}
                aria-hidden
              />
              <span className="flex-1 truncate">{status.name}</span>
              {status.id === value && <Check className="size-3.5 text-[var(--accent)]" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ── Priority ──────────────────────────────────────────── */

export function PrioritySelect({
  value,
  onChange,
  disabled,
}: {
  value: Priority;
  onChange: (p: Priority) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FieldButton disabled={disabled} data-testid="task-priority-select" aria-label="Priority">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ background: PRIORITY_COLOUR[value] }}
            aria-hidden
          />
          <span>{value}</span>
          <ChevronDown className="ml-auto size-3.5 shrink-0 opacity-60" />
        </FieldButton>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        {PRIORITIES.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => {
              onChange(p);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[13px] hover:bg-[var(--surface-2)]"
          >
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ background: PRIORITY_COLOUR[p] }}
              aria-hidden
            />
            <span className="font-medium">{p}</span>
            <span className="vt-muted">— {PRIORITY_MEANING[p]}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/* ── Type ──────────────────────────────────────────────── */

export function TypeSelect({
  value,
  onChange,
  disabled,
}: {
  value: TaskType;
  onChange: (t: TaskType) => void;
  disabled?: boolean;
}) {
  return (
    <select
      aria-label="Type"
      data-testid="task-type-select"
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value as TaskType)}
      className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
    >
      {TASK_TYPES.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}

/* ── People ────────────────────────────────────────────── */

/**
 * Assignee picker — §8.4, with the F5 suggestion chips at the top.
 *
 * Multi-select for assignees, single for reviewer. Delegation (V3 R1) needs no
 * special case here: the server decides whether the caller may reassign, and a
 * denial surfaces as a toast from the caller's mutation.
 */
export function PeoplePicker({
  value,
  onChange,
  multiple = true,
  label,
  suggestFor,
  disabled,
  testId,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
  label: string;
  suggestFor?: { projectId?: string; type?: string; estimateMinutes?: number; dueDate?: string };
  disabled?: boolean;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const { data: people = [] } = useUsers();
  const { data: suggestions = [] } = useSuggestedAssignees(suggestFor ?? {});

  const active = people.filter((p) => p.status === 'active');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return active;
    return active.filter(
      (p) =>
        p.name.toLowerCase().includes(needle) ||
        p.email.toLowerCase().includes(needle) ||
        p.designation?.toLowerCase().includes(needle) ||
        (p.skills ?? []).some((s) => s.toLowerCase().includes(needle)),
    );
  }, [active, q]);

  const selected = active.filter((p) => value.includes(p._id));

  const toggle = (id: string): void => {
    if (!multiple) {
      onChange(value.includes(id) ? [] : [id]);
      setOpen(false);
      return;
    }
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <FieldPopover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FieldButton disabled={disabled} aria-label={label} data-testid={testId}>
          {selected.length === 0 ? (
            <span className="vt-muted">Unassigned</span>
          ) : (
            <span className="flex min-w-0 items-center gap-1">
              {selected.slice(0, 3).map((p) => (
                <UserAvatar key={p._id} id={p._id} name={p.name} size={18} />
              ))}
              <span className="truncate">
                {selected.length === 1 ? selected[0].name : `${selected.length} people`}
              </span>
            </span>
          )}
          <ChevronDown className="ml-auto size-3.5 shrink-0 opacity-60" />
        </FieldButton>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="border-b border-[var(--border)] p-1.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, designation or skill…"
              aria-label={`Search for ${label}`}
              className="h-7 pl-7"
            />
          </div>
        </div>

        {suggestions.length > 0 && !q && (
          <div className="border-b border-[var(--border)] p-1.5">
            <div className="mb-1 px-1 text-[10px] uppercase tracking-wide vt-muted">Suggested</div>
            <div className="flex flex-wrap gap-1">
              {suggestions.slice(0, 3).map((s) => (
                <button
                  key={s.userId}
                  type="button"
                  onClick={() => toggle(s.userId)}
                  title={s.reasons.join(' · ')}
                  className="flex items-center gap-1 rounded-full border border-[var(--border)] px-1.5 py-0.5 text-[11px] hover:bg-[var(--surface-2)]"
                >
                  <UserAvatar id={s.userId} name={s.name} size={14} />
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={cn(PICKER_LIST_HEIGHT, 'p-1')} data-testid="picker-list">
          {filtered.length === 0 ? (
            <p className="px-2 py-3 text-center text-[12px] vt-muted">Nobody matches.</p>
          ) : (
            filtered.map((p) => (
              <button
                key={p._id}
                type="button"
                onClick={() => toggle(p._id)}
                className="flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[13px] hover:bg-[var(--surface-2)]"
              >
                <UserAvatar id={p._id} name={p.name} size={20} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{p.name}</span>
                  <span className="block truncate text-[11px] vt-muted">{p.designation}</span>
                </span>
                {value.includes(p._id) && <Check className="size-3.5 text-[var(--accent)]" />}
              </button>
            ))
          )}
        </div>

        {multiple && value.length > 0 && (
          <div className="border-t border-[var(--border)] p-1.5">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[12px] vt-muted hover:text-[var(--text)]"
            >
              Clear all
            </button>
          </div>
        )}
      </PopoverContent>
    </FieldPopover>
  );
}

/* ── Labels ────────────────────────────────────────────── */

export function LabelPicker({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { data: labels = [] } = useLabels();
  const selected = labels.filter((l) => value.includes(l._id));

  return (
    <FieldPopover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FieldButton disabled={disabled} aria-label="Labels" data-testid="task-labels">
          {selected.length === 0 ? (
            <span className="vt-muted">No labels</span>
          ) : (
            <span className="flex flex-wrap gap-1">
              {selected.slice(0, 3).map((l) => (
                <Badge key={l._id} tone="neutral">
                  {l.name}
                </Badge>
              ))}
              {selected.length > 3 && <span className="vt-muted">+{selected.length - 3}</span>}
            </span>
          )}
          <ChevronDown className="ml-auto size-3.5 shrink-0 opacity-60" />
        </FieldButton>
      </PopoverTrigger>
      <PopoverContent className="max-h-64 w-56 overflow-y-auto p-1" align="start">
        {labels.length === 0 ? (
          <p className="px-2 py-3 text-center text-[12px] vt-muted">
            No labels yet — create them in Settings.
          </p>
        ) : (
          labels.map((l) => (
            <button
              key={l._id}
              type="button"
              onClick={() =>
                onChange(value.includes(l._id) ? value.filter((v) => v !== l._id) : [...value, l._id])
              }
              className="flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[13px] hover:bg-[var(--surface-2)]"
            >
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ background: l.colour }}
                aria-hidden
              />
              <span className="flex-1 truncate">{l.name}</span>
              {value.includes(l._id) && <Check className="size-3.5 text-[var(--accent)]" />}
            </button>
          ))
        )}
      </PopoverContent>
    </FieldPopover>
  );
}

/* ── Dependencies ──────────────────────────────────────── */

/** Picks another task in the same project, excluding this one. */
export function TaskPicker({
  projectId,
  excludeId,
  onPick,
  label,
  testId,
}: {
  projectId?: string;
  excludeId?: string;
  onPick: (task: TaskView) => void;
  label: string;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const { data } = useTasksForPicker(projectId, open);
  const options = (data ?? [])
    .filter((t) => t._id !== excludeId)
    .filter((t) => !q || t.title.toLowerCase().includes(q.toLowerCase()) || t.key.includes(q.toUpperCase()));

  return (
    <FieldPopover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FieldButton aria-label={label} data-testid={testId} className="h-7 text-[12px]">
          {label}
        </FieldButton>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="border-b border-[var(--border)] p-1.5">
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Key or title…"
            aria-label="Find a task"
            className="h-7"
          />
        </div>
        <div className={cn(PICKER_LIST_HEIGHT, 'p-1')} data-testid="picker-list">
          {options.slice(0, 40).map((t) => (
            <button
              key={t._id}
              type="button"
              data-testid="task-option"
              onClick={() => {
                onPick(t);
                setOpen(false);
                setQ('');
              }}
              className="flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[13px] hover:bg-[var(--surface-2)]"
            >
              <span className="vt-mono shrink-0 text-[11px] vt-muted">{t.key}</span>
              <span className="truncate">{t.title}</span>
            </button>
          ))}
          {options.length === 0 && (
            <p className="px-2 py-3 text-center text-[12px] vt-muted">Nothing matches.</p>
          )}
        </div>
      </PopoverContent>
    </FieldPopover>
  );
}

/** Local helper so `TaskPicker` does not pull the whole list until opened. */
function useTasksForPicker(projectId: string | undefined, enabled: boolean) {
  const result = useTasks(
    { projectId, limit: 200, fields: 'summary' },
    { enabled: enabled && Boolean(projectId) },
  );
  return { data: result.data?.items as TaskView[] | undefined };
}

/* ── Small inputs ──────────────────────────────────────── */

export function DateField({
  value,
  onChange,
  label,
  disabled,
  testId,
}: {
  value?: string | null;
  onChange: (v: string | null) => void;
  label: string;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="date"
        aria-label={label}
        data-testid={testId}
        disabled={disabled}
        value={value ? value.slice(0, 10) : ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
      />
      {value && !disabled && (
        <button
          type="button"
          aria-label={`Clear ${label}`}
          onClick={() => onChange(null)}
          className="grid size-6 place-items-center rounded-[4px] vt-muted hover:bg-[var(--surface-2)]"
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}

/** Estimates are stored in minutes; people think in hours. */
export function EstimateField({
  minutes,
  onChange,
  disabled,
}: {
  minutes?: number | null;
  onChange: (minutes: number | null) => void;
  disabled?: boolean;
}) {
  const [text, setText] = useState(minutes ? String(minutes / 60) : '');

  return (
    <div className="flex items-center gap-1">
      <Input
        aria-label="Estimate in hours"
        data-testid="task-estimate"
        disabled={disabled}
        value={text}
        inputMode="decimal"
        placeholder="—"
        className="h-8 w-20"
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const hours = Number.parseFloat(text);
          onChange(Number.isFinite(hours) && hours > 0 ? Math.round(hours * 60) : null);
        }}
      />
      <span className="text-[12px] vt-muted">hours</span>
    </div>
  );
}
